import React, { useEffect } from 'react';
import axios from 'axios';
import { configuration } from '../../configuration';
import { secureAxiosInstance } from '../../api-service';
import { TenantAddonsClient, ITenantAddonsClient, TenantAddonConfigurationValue } from '../../api-service/clients/auto-generated-clients';

export interface ITenantAddons {
  isTenantAddonEnabled: (addonNames: string[]) => boolean;
  getAddonConfiguration: (configurationName: string) => string | undefined;
  tenantAddons: string[] | undefined;
}

const TenantAddonsContext = React.createContext({} as ITenantAddons);

type TenantAddonsProviderProps = {
  children: React.ReactNode;
};

export function TenantAddonsProvider({ children }: TenantAddonsProviderProps) {
  const [tenantAddons, setTenantAddons] = React.useState<string[] | undefined>(undefined);
  const [addonConfiguration, setAddonConfiguration] = React.useState<TenantAddonConfigurationValue[]>([]);

  const client: ITenantAddonsClient = new TenantAddonsClient(configuration.bffBase, secureAxiosInstance);

  function isTenantAddonEnabled(addonNames: string[]): boolean {
    if (tenantAddons === undefined) {
      return false;
    }
    return addonNames.some(name => tenantAddons.includes(name.toLocaleLowerCase()));
  }

  function getAddonConfiguration(configurationName: string): string | undefined {
    return addonConfiguration.find(config => config.configurationName?.toLocaleLowerCase() === configurationName.toLocaleLowerCase())?.value;
  }

  React.useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    async function loadEnabledTenantAddons() {
      try {
        const response = await client.getEnabledTenantAddons(source.token);
        if (isMounted) {
          setTenantAddons(Array.isArray(response?.enabledTenantAddons) ? response.enabledTenantAddons : []);
        }
      } catch (error: any) {
        if (axios.isCancel(error)) {
          console.info('Request canceled', error.message);
        }
        if (isMounted) {
          setTenantAddons([]);
        }
      }
    }

    loadEnabledTenantAddons();

    return () => {
      isMounted = false;
      source.cancel();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    async function loadAddonConfiguration() {
      try {
        const response = await client.getEnabledTenantAddonConfigurations(source.token);
        if (isMounted) {
          setAddonConfiguration(Array.isArray(response?.extraConfigurations) ? response.extraConfigurations : []);
        }
      } catch (error: any) {
        if (axios.isCancel(error)) {
          console.info('Request canceled', error.message);
        }
        if (isMounted) {
          setAddonConfiguration([]);
        }
      }
    }

    loadAddonConfiguration();

    return () => {
      isMounted = false;
      source.cancel();
    };
  }, []);

  const contextValue = React.useMemo(() => ({ isTenantAddonEnabled, getAddonConfiguration, tenantAddons }), [tenantAddons, addonConfiguration]);

  return <TenantAddonsContext.Provider value={contextValue}>{children}</TenantAddonsContext.Provider>;
}

export const useTenantAddons = (): ITenantAddons => React.useContext(TenantAddonsContext);

export enum TenantAddonConstants {
  Virksomhedsservice = 'virksomhedsservice',
  Arbejdskraftbehov = 'arbejdskraftbehov',
  FormidlingAfStoettedeJob = 'formidlingAfStoettedeJob',
  SaerligeForholdForVirksomheder = 'SaerligeForholdForVirksomheder',
  Virksomhedstags = 'Virksomhedstags',
  JobAds = 'enableJobAds',
  QuickJournalNote = 'quickJournalNote',
  CitizenPortalJournalReportAccess = 'citizenPortalJournalReportAccess',
  SocialMyndighed = 'SocialMyndighed',
  SocialUdfoerer = 'SocialUdfører',
  ConfidentialCases = 'ConfidentialCases',
  MedicalCollaboration = 'MedicalCollaboration',
  CoPayment = 'CoPayment',
  CitizenAccessJustification = 'CitizenAccessJustification',
  CitizenIncome = 'CitizenIncome',
  Lexi = 'AiFasitAssistant',
  Summa = 'AiCitizenAssistant',
  Dialogstoette = 'Dialogstøtte',
  VumDialogSupport = 'VumDialogSupport',
  MyndighedsOptik = 'MyndighedsOptik',
  AssignmentAiRewrite = 'AssignmentAiRewrite',
  Sygehusadvis = 'Sygehusadvis',
  AiLetterFill = 'AiLetterFill',
  AiSmartGoalAssistant = 'AiSmartGoalAssistant',
  JointCalendar = 'JointCalendar',
  VisitationAssistant = 'VisitationAssistant',
  Ledelsesinformation = 'Ledelsesinformation',
}
